// Field and taxonomy settings of a type (R-U3): show or hide columns, order, add fields, rename, archive, and edit terms and pipeline stages.

import { pipelineSectionOf } from '../pipelines/pipeline-section.ts';
import { useState } from 'react';
import { FIELD_TYPES, fieldLock, peopleFields, uuidv7, type Cardinality, type FieldDef, type FieldType, type Id, type TaxonomyStyle, type ViewColumn } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { FIELD_ICONS, Icon } from '../ui/icons.tsx';
import { Button, Checkbox, Chevron, IconButton, Input, Menu, MenuContent, MenuItem, MenuTrigger, Segmented, cx, useToast } from '../ui/primitives.tsx';
import { SYSTEM_COLUMNS } from './system-columns.tsx';
import { TermsEditor } from '../schema/TermsEditor.tsx';
import { ArchiveConfirm } from '../schema/ArchiveConfirm.tsx';
import { CardEye, inCard, resetCardEye } from './CardEye.tsx';
import s from './settings.module.css';

const ADDABLE: FieldType[] = ['text', 'longtext', 'number', 'money', 'date', 'email', 'phone', 'url', 'checkbox', 'taxonomy', 'relation', 'user', 'query'];

export function FieldSettings({ typeId, columns, onColumns, onClose, startAdding }: { typeId: Id; columns: ViewColumn[]; onColumns: (c: ViewColumn[]) => void; onClose: () => void; startAdding?: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const type = meta.type(typeId);
  const isOwner = meta.user(client.header?.userId)?.role === 'owner';
  const [adding, setAdding] = useState(!!startAdding);
  const [openTax, setOpenTax] = useState<Id | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [names, setNames] = useState({ one: '', many: '' });
  const fields = peopleFields(meta.fields(typeId));
  const inView = new Map(columns.map((c) => [c.field, c]));

  const toggle = (key: string) => {
    const c = inView.get(key);
    if (!c) onColumns([...columns, { field: key, width: 180 }]);
    else onColumns(columns.map((x) => (x.field === key ? { ...x, hidden: !x.hidden } : x)));
  };
  const visible = (key: string) => { const c = inView.get(key); return !!c && !c.hidden; };

  return (
    <aside className={s.panel} aria-label="Поля и таксономии">
      <header className={s.head}>
        {renaming && type ? (
          <form className={s.rename} onSubmit={(e) => { e.preventDefault(); const one = names.one.trim(), many = names.many.trim(); if (one && many) client.mutate('type.update', { id: type.id, name: one, nameMany: many }, [{ kind: 'type', id: type.id, op: 'patch', data: { name: one, nameMany: many } }]); setRenaming(false); }}>
            <Input autoFocus aria-label="Раздел во множественном числе" placeholder="Например, Площадки" value={names.many} onChange={(e) => setNames({ ...names, many: e.target.value })} />
            <Input aria-label="Одна запись" placeholder="Например, Площадка" value={names.one} onChange={(e) => setNames({ ...names, one: e.target.value })} />
            <div className={s.renameActions}><Button size="sm" onClick={() => setRenaming(false)}>Отмена</Button><Button size="sm" variant="primary" type="submit">Сохранить</Button></div>
          </form>
        ) : (
          <div>
            <div className={s.title}>Поля · {type?.nameMany}
              {/* A section made in the interface is renamed by the owner (R-C1); an app's section keeps the app's name. */}
              {isOwner && type && !type.app && <IconButton size="sm" icon="edit" label="Переименовать раздел" onClick={() => { setNames({ one: type.name, many: type.nameMany }); setRenaming(true); }} />}
            </div>
            <div className={s.sub}>Колонки этого вида, свойства карточки и структура раздела</div>
          </div>
        )}
        <IconButton icon="close" label="Закрыть" onClick={onClose} />
      </header>
      <div className={s.body}>
        <div className={s.eyeHead} aria-hidden>Глазок: колонка в виде · свойство в карточке</div>
        {fields.map((f) => (
          <FieldRow key={f.id} field={f} visible={visible(f.key)} onToggle={() => toggle(f.key)} isOwner={isOwner} card={inCard(f, type?.titleField)}
            expanded={openTax === f.id} onExpand={() => setOpenTax(openTax === f.id ? null : f.id)} />
        ))}
        <ArchivedFields typeId={typeId} isOwner={isOwner} />
        <div className={s.group}>Служебные</div>
        {Object.entries(SYSTEM_COLUMNS).map(([k, v]) => (
          <div key={k} className={s.row}>
            <Icon name={v.icon} size={15} className={s.ficon} />
            <span className={s.fname}>{v.label}</span>
            <IconButton size="sm" icon={visible(k) ? 'eye' : 'eyeOff'} className={cx(s.eye, visible(k) && s.eyeOn)} aria-pressed={visible(k)} onClick={() => toggle(k)} label={visible(k) ? `Скрыть «${v.label}»` : `Показать «${v.label}»`} />
            <span className={s.eyeGap} />
          </div>
        ))}
      </div>
      {isOwner && (
        <footer className={s.foot}>
          {adding ? <AddField typeId={typeId} onDone={(key) => { setAdding(false); if (key) onColumns([...columns, { field: key, width: 180 }]); }} />
            : <Button variant="secondary" icon="plus" onClick={() => setAdding(true)}>Добавить поле</Button>}
        </footer>
      )}
    </aside>
  );
}

function FieldRow({ field, visible, onToggle, isOwner, card, expanded, onExpand }: { field: FieldDef; visible: boolean; onToggle: () => void; isOwner: boolean; card: boolean; expanded: boolean; onExpand: () => void }) {
  const client = useSync();
  const meta = useMeta();
  const toast = useToast();
  const [name, setName] = useState<string | null>(null);
  const tax = field.fieldType === 'taxonomy' ? meta.taxonomy(field.options.taxonomyId) : undefined;
  const lock = fieldLock(meta, field);
  const [archiving, setArchiving] = useState(false);
  const archive = async () => {
    const r = await client.run('field.archive', { id: field.id });
    toast(r.ok ? { kind: 'info', text: `Поле «${field.name}» в архиве. Значения сохранены, его можно вернуть внизу списка.` } : { kind: 'error', text: r.error.message });
  };
  const target = field.fieldType === 'relation' ? meta.relationTarget(field) : undefined;
  const hint = tax ? { labels: 'метки', tree: 'дерево', pipeline: 'воронка' }[tax.style] : target ? `→ ${meta.type(target.typeId)?.nameMany ?? ''}${target.many ? ', несколько' : ''}` : FIELD_TYPES[field.fieldType].label.toLowerCase();
  return (
    <div className={s.item}>
      <div className={s.row}>
        <Icon name={FIELD_ICONS[field.fieldType] ?? 'text'} size={15} className={s.ficon} />
        {name !== null ? (
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} className={s.smallInput}
            onBlur={() => setName(null)}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) { client.mutate('field.update', { id: field.id, name: name.trim() }); setName(null); } if (e.key === 'Escape') setName(null); }} />
        ) : (
          <span className={s.fname} onDoubleClick={() => isOwner && setName(field.name)}>
            {field.name}{field.required && <span className={s.req} title="Обязательное">*</span>}
            <span className={s.fhint}>{hint}</span>
            {field.inNav && <span className={s.fhint}> · в колонке</span>}
          </span>
        )}
        {field.system && <span className={s.lock} title={lock.archive ? 'Поле приложения: имя меняется, ключ и устройство — нет' : `Поле приложения: ${lock.why}`}><Icon name="lock" size={12} /></span>}
        {tax && <Button size="sm" iconOnly aria-label="Значения" aria-expanded={expanded} onClick={onExpand}><Chevron dir="right" open={expanded} /></Button>}
        {isOwner && (
          <Menu>
            <MenuTrigger asChild><IconButton size="sm" icon="more" label="Действия с полем" /></MenuTrigger>
            <MenuContent align="end">
              <MenuItem icon="text" onSelect={() => setName(field.name)}>Переименовать</MenuItem>
              {!field.system && <MenuItem icon="tick" onSelect={() => client.mutate('field.update', { id: field.id, required: !field.required })}>{field.required ? 'Сделать необязательным' : 'Сделать обязательным'}</MenuItem>}
              {card && field.card && <MenuItem icon="eye" onSelect={() => resetCardEye(client, field)}>В карточке — как по умолчанию</MenuItem>}
              {/* The values of the field as a tree in the left column (um/2026-10-30/03 п.7); kept on the server for the section. */}
              {tax && !(tax.style === 'pipeline' && pipelineSectionOf(meta.type(field.typeId)?.key)) && <MenuItem icon="sidebar" onSelect={() => void client.run('field.update', { id: field.id, inNav: !field.inNav }).then((r) => { if (!r.ok) toast({ kind: 'error', text: r.error.message }); })}>{field.inNav ? 'Убрать из левой колонки' : 'Показывать в левой колонке'}</MenuItem>}
              {lock.archive ? <MenuItem icon="archive" danger onSelect={() => setArchiving(true)}>В архив</MenuItem> : <MenuItem icon="lock" disabled>{lock.why}</MenuItem>}
            </MenuContent>
          </Menu>
        )}
        <IconButton size="sm" icon={visible ? 'eye' : 'eyeOff'} className={cx(s.eye, visible && s.eyeOn)} aria-pressed={visible} onClick={onToggle} label={visible ? 'Колонка видна в этом виде — скрыть' : 'Колонка скрыта в этом виде — показать'} />
        {card ? <CardEye field={field} canEdit={isOwner} /> : <span className={s.eyeGap} />}
      </div>
      {tax && expanded && <TermsEditor taxonomyId={tax.id} canEdit={isOwner} />}
      {archiving && <ArchiveConfirm kind="field" id={field.id} label={field.name} onConfirm={() => void archive()} onClose={() => setArchiving(false)} />}
    </div>
  );
}

function ArchivedFields({ typeId, isOwner }: { typeId: Id; isOwner: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const list = meta.archived<FieldDef>('field', (f) => f.typeId === typeId);
  if (!isOwner || !list.length) return null;
  return (
    <>
      <button type="button" className={cx(s.group, s.groupToggle)} aria-expanded={open} onClick={() => setOpen(!open)}><Chevron dir="right" open={open} size={12} />Архив полей: {list.length}</button>
      {open && list.map((f) => (
        <div key={f.id} className={s.row}>
          <Icon name={FIELD_ICONS[f.fieldType] ?? 'text'} size={15} className={s.ficon} />
          <span className={s.fname}>{f.name}</span>
          <Button size="sm" variant="ghost" onClick={async () => { const r = await client.run('field.restore', { id: f.id }); toast(r.ok ? { kind: 'info', text: `Поле «${f.name}» вернулось со своими значениями` } : { kind: 'error', text: r.error.message }); }}>Вернуть</Button>
        </div>
      ))}
    </>
  );
}

function AddField({ typeId, onDone }: { typeId: Id; onDone: (key: string | null) => void }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const [name, setName] = useState('');
  const [ft, setFt] = useState<FieldType>('text');
  const [style, setStyle] = useState<TaxonomyStyle>('labels');
  const [taxonomyId, setTaxonomyId] = useState<Id | null>(null);
  const [multiple, setMultiple] = useState(false);
  const [target, setTarget] = useState<Id | null>(null);
  const [card, setCard] = useState<Cardinality>('many-to-one');
  const types = meta.types();
  const create = () => {
    if (!name.trim()) return;
    if (ft === 'relation' && !target) { toast({ kind: 'error', text: 'Выберите, с чем связать' }); return; }
    const id = uuidv7();
    const before = new Set(meta.fields(typeId).map((f) => f.key));
    client.mutate('field.create', { id, typeId, name: name.trim(), fieldType: ft, options: { style, multiple, taxonomyId: ft === 'taxonomy' ? taxonomyId ?? undefined : undefined, targetTypeId: target, cardinality: card, currency: ft === 'money' ? 'RUB' : undefined } });
    // The server picks the machine key; the new column appears as soon as the field arrives.
    const wait = client.store.subscribe(() => {
      const f = meta.fields(typeId).find((x) => !before.has(x.key) && x.id === id);
      if (f) { wait(); onDone(f.key); }
    });
    setTimeout(wait, 5000);
  };
  return (
    <div className={s.add} onKeyDown={(e) => { if (e.key === 'Enter') create(); if (e.key === 'Escape') onDone(null); }}>
      <Input autoFocus placeholder="Название поля" value={name} onChange={(e) => setName(e.target.value)} />
      <div className={s.types} role="radiogroup" aria-label="Тип поля">
        {ADDABLE.map((t) => (
          <button key={t} type="button" role="radio" aria-checked={ft === t} className={cx(s.typeBtn, ft === t && s.typeOn)} onClick={() => setFt(t)}>
            <Icon name={FIELD_ICONS[t]!} size={14} />{t === 'taxonomy' ? 'Метки или воронка' : FIELD_TYPES[t].label}
          </button>
        ))}
      </div>
      {ft === 'taxonomy' && (
        <div className={s.opts}>
          <Menu>
            <MenuTrigger asChild><Button size="sm" variant="secondary" caret>{taxonomyId ? meta.taxonomy(taxonomyId)?.name : 'Новая таксономия'}</Button></MenuTrigger>
            <MenuContent>
              <MenuItem onSelect={() => setTaxonomyId(null)}>Новая таксономия</MenuItem>
              {meta.taxonomies().map((t) => <MenuItem key={t.id} onSelect={() => { setTaxonomyId(t.id); setStyle(t.style); }}>{t.name}</MenuItem>)}
            </MenuContent>
          </Menu>
          {!taxonomyId && <Segmented size="sm" label="Вид таксономии" value={style} onChange={setStyle} items={[{ value: 'labels', label: 'Метки' }, { value: 'tree', label: 'Дерево' }, { value: 'pipeline', label: 'Воронка' }]} />}
          {style === 'labels' && <Checkbox className={s.check} checked={multiple} onChange={setMultiple}>несколько</Checkbox>}
        </div>
      )}
      {ft === 'relation' && (
        <div className={s.opts}>
          <Menu>
            <MenuTrigger asChild><Button size="sm" variant="secondary" caret>{meta.type(target)?.nameMany ?? 'Связать с…'}</Button></MenuTrigger>
            <MenuContent>{types.map((t) => <MenuItem key={t.id} icon={t.icon} onSelect={() => setTarget(t.id)}>{t.nameMany}</MenuItem>)}</MenuContent>
          </Menu>
          <Segmented size="sm" label="Сколько записей" value={card} onChange={setCard} items={[{ value: 'many-to-one', label: 'одна запись' }, { value: 'many-to-many', label: 'несколько' }]} />
        </div>
      )}
      <div className={s.opts}>
        <span className={s.spacer} />
        <Button size="sm" onClick={() => onDone(null)}>Отмена</Button>
        <Button size="sm" variant="primary" onClick={create} disabled={!name.trim()}>Создать поле</Button>
      </div>
    </div>
  );
}
